/**
 * 後台全域搜尋詞庫（業務詞 → 導覽入口 id）
 *
 * 用途：側欄搜尋除了比對顯示標籤，也比對此處別名、頁內文案與常見 SOP 用語。
 * 維護原則：
 * - key 必須是 `ADMIN_NAV_SECTIONS` 的 section.id 或 leaf.id
 * - 寫「使用者可能打的詞」，不要重複已經出現在 label / pageTitle 的字（可重複也無害）
 * - 專有名詞可同時放中文與常見英文縮寫（ET、LJ、BESTEP、gate）
 * - 不要把太泛用的詞放到 section 層（例如「設定」），以免一次展開整區
 *
 * @type {Record<string, string[]>}
 */
export const ADMIN_NAV_SEARCH_GLOSSARY = {
  events: ['活動營運', '場次', '預約'],
  'events-list': [
    '新增活動',
    '修改活動',
    '刪除活動',
    '活動明細',
    '簽到',
    '刷卡',
    'check-in',
    '預約名單',
    '匯出名單',
    'English Table',
    'English Club',
    'Job Talk',
    '場次管理',
  ],
  'events-participation-checkins': [
    '簽到率',
    '出席',
    '未到',
    'no-show',
    '參與率',
    'check-in',
  ],
  'et-grouping-settings': [
    '英語桌',
    'English Table',
    '分組',
    '指派',
    'Leader',
    '帶班',
    'grouping',
  ],
  'et-task-templates': ['任務勾選', '模板', 'task', '英語桌任務'],
  'et-grouping-reports': ['場次彙總', '英語桌報表', 'ET 報表'],
  'et-leader-payroll': ['支薪', '薪水', '時薪', '年資', 'Leader 薪資', 'payroll'],
  'et-student-trends': ['學期趨勢', '英語桌學生', 'ET 趨勢'],
  'et-leader-sessions': [
    '帶班',
    '我的場次',
    'Leader',
    '任務勾選',
    '英語桌帶班',
  ],
  'et-grouping': ['ET', '英語桌', '分組', 'Leader', '帶班'],
  'events-compliance': [
    '違規',
    '黑名單',
    'blacklist',
    '未到',
    '缺席',
    'no-show',
    '登記違規',
    '解封',
    '違規管理',
  ],

  classes: [
    '班級概況',
    '名冊',
    'roster',
    'BESTEP 班級',
    '修課名單',
    '我的班級',
  ],
  'classes-overview': [
    '班級列表',
    '班級參與',
    '修課名冊',
  ],
  'class-credit-allocation': [
    '課堂加分',
    '時數分配',
    '活動時數',
    '計點',
  ],

  english: ['英檢', '培力', 'BESTEP', '學習歷程', '護照'],
  'english-learning-passport': [
    'ELP',
    '護照',
    '審核提交',
    '實踐歷程',
    '英語實踐',
    'submission',
  ],
  'english-registration': [
    '培力英檢',
    'BESTEP',
    '報名審核',
    '待審',
    '個人報名',
    '團體報名',
    '錄取',
    '英檢管理',
  ],
  'learning-outcomes': ['學習成果', '學習歷程', '護照', '成效', 'LA', 'B2', 'CEFR'],
  'learning-analytics': ['學習成效', 'LA', '成效分析'],
  'learning-journey': [
    'LJ',
    'LJ V3',
    '學習歷程',
    '學號',
    '查學生',
    'B2',
    'CEFR',
    '學生清單',
    'Student Table',
    '分項統計',
    'Breakdown',
    '四技能',
    'canonical',
  ],
  'learning-journey-import': [
    '名冊匯入',
    '英檢成績匯入',
    'Excel',
    '上傳',
    'LJ 匯入',
  ],
  'learning-journey-ewl-sync': [
    '寫作工坊',
    'EWL',
    '同步',
    'Consultation',
  ],
  'learning-journey-operations': [
    'projection',
    '重建',
    '資料新鮮度',
    'operation run',
  ],
  'bestep-import': ['BESTEP', '培力英檢匯入', 'LR', 'SW', '出席匯入'],
  'speaking-diagnostic': [
    '口說',
    '口說診斷',
    'speaking',
    'fluency',
    'pronunciation',
    '錄音',
    '逐字比對',
    'rubric',
  ],

  surveys: ['問卷', '回饋', 'gate', '作答'],
  'survey-ops': ['問卷維運', '資料品質', '答案對照'],
  'survey-center': [
    '建立問卷',
    '發布',
    '題目',
    '草稿',
    '作答紀錄',
    '統計',
    'survey',
    '匯出問卷',
  ],
  'survey-rules-new': [
    'gate',
    '問卷 gate',
    '啟用',
    '必填',
    'ET',
    'EC',
    '學期規則',
    '何時要填',
    '活動類型',
  ],
  'survey-responses': ['作答紀錄', '問卷回覆', 'responses', '填答'],
  'survey-analytics': ['問卷分析', '統計分析', 'survey analytics'],
  'survey-health': [
    '資料品質',
    '修復',
    'gap',
    '缺學期',
    '維運',
    '問題清單',
  ],
  'survey-answer-mappings': [
    '對照',
    'mapping',
    '題目代碼',
    '核准',
    '舊代碼',
  ],
  'survey-manage': ['舊版問卷', 'Excel 統計', 'legacy'],
  'survey-product': ['問卷模組', 'survey-module'],

  'import-center': ['匯入', '上傳', '同步', 'Excel'],
  'import-center-home': [
    '上傳',
    '匯入',
    '同步',
    'Excel',
    '範本',
    '名冊',
    'BESTEP',
    '刷卡簽到',
    'EWL',
    '修課名單',
  ],
  'import-center-runs': [
    '匯入紀錄',
    '回滾',
    '批次',
    'import run',
    '稽核摘要',
    '刪除批次',
  ],

  'la-report': ['學期達標', '正式報表', '聽讀成對'],
  'la-people': ['學生名單', '尚未達標', '同一批'],
  'la-resources': ['課師活動', '參與覆蓋'],
  'la-compare': ['系所差異', 'GSE 比較', '原始匯出'],
  'learning-analytics-overview': ['KPI', '總覽', '趨勢比較', '中心成效'],
  'learning-analytics-kpi-report': ['B2 KPI', '一鍵報表', 'KPI 報表'],
  'learning-analytics-cohorts': ['入學屆別', 'cohort', '群體'],
  'learning-analytics-offerings': ['細項分析', 'offerings', '課程細項', '活動細項'],
  'learning-analytics-resources': ['課程效益', '活動效益', '資源'],
  'learning-analytics-skills': ['四技能', 'GSE', '技能成長'],
  'learning-analytics-b1-plus': ['接近 B2', 'B1+', 'GSE', '前測', '修課', '活動參與'],
  'learning-analytics-explore': ['學生探索', '篩選名單', '起始分級', '前測', '後測', '活動時數', '修課'],
  'learning-analytics-tracking': ['追蹤名單', '固定名單', '前後對照', '同一批學生'],
  'learning-analytics-students': ['學習軌跡', '學生歷程', '個人分析'],
  'learning-analytics-raw': ['raw data', '原始資料', '明細資料'],
  'learning-analytics-settings': ['LVA', '公式', '模組設定', '分析設定', '資料版本'],

  analytics: ['報表', '分析', 'KPI'],
  'analytics-students': ['學號搜尋', '舊查詢', '學生查詢'],
  'analytics-overview': ['活動營運', '預約率', '名額利用率', '簽到', '違規'],
  'analytics-risk': ['高風險', '行政追蹤', '輔導名單'],
  'analytics-reports': ['下載', 'Excel', '匯出報表', '營運報表'],
  'analytics-teacher-dash': ['教學儀表板', '我的教學', '老師儀表板'],

  content: ['內容', '公告', '週報', '學生端'],
  announcements: ['草稿', '發布', '跑馬燈', 'ticker', '公告'],
  'weekly-reports': ['週報', 'Weekly Studio', 'blocks', '本週'],
  'student-content': [
    '首頁',
    '活動介紹',
    '關於我們',
    '法規',
    '法規表單',
    '學習資源',
    '修課說明',
    '頁面內容',
    '學生看到的',
    'Header',
  ],

  accounts: ['帳號', '權限', '角色'],
  'account-list': [
    '帳號',
    '權限',
    'RBAC',
    '角色',
    '工讀生',
    'Leader',
    '覆寫權限',
  ],
  'account-reset': ['密碼', '重設密碼', 'mustResetPassword'],

  system: ['維運', '稽核'],
  'system-config': ['設定', '活動類型', '郵件'],
  'system-audit': ['稽核', '操作紀錄', '診斷', '維運腳本'],
  'system-dashboard': ['KPI', '今日待辦', '健康', '營運', 'dashboard'],
  'system-settings': [
    '開關',
    '功能開關',
    '系統開關',
    '英檢個人報名開關',
    '英檢報名開關',
    '檢視與修正開關',
    '個人報名',
    '團體報名',
    'Learning Partner',
    '學習有伴',
    '報名按鈕',
    'feature flag',
  ],
  'event-types-settings': [
    '活動類型',
    'event type',
    '截止時數',
    'cutoff',
    '開放規則',
    '組數設定',
  ],
  'system-email-templates': [
    '郵件',
    'email',
    '通知信',
    '模板',
    '寄信',
    '驗證碼',
    '實踐歷程',
    '英語學習護照',
    'OTP',
  ],
  'system-logs': [
    'audit',
    'log',
    '稽核',
    'requestId',
    '操作紀錄',
    '日誌',
  ],
  'system-diagnostics': ['診斷', 'ping', '健康檢查', 'API 診斷'],
  'system-ops-scripts': ['備份', 'backup', '寫作工坊', 'EWL', '同步', '腳本', '維運'],
  'system-trip-count': ['分帳', '旅遊', 'TripCount', '結算', '轉帳'],
  'analytics-hub': ['成效分析中心', '分析入口', 'analytics hub'],
};

/**
 * @param {string} navId
 * @returns {string[]}
 */
export function getAdminNavSearchTerms(navId) {
  const terms = ADMIN_NAV_SEARCH_GLOSSARY[navId];
  return Array.isArray(terms) ? terms : [];
}
